import { Observable, EMPTY, of } from 'rxjs';
import {
  map,
  mergeMap,
  filter,
  skipWhile,
  tap,
  ignoreElements,
  distinctUntilChanged,
} from 'rxjs/operators';
import { StateObservable } from 'redux-observable';

import { PortableEvent } from '@sas-dvr/ltjs-commons';
import { DataStateChangeEvent, PromptElementController } from '@sas-dvr/ltjs-bird';

import { ReportState } from '@sas-dvr/internal-va-react-core/store/rootReducer';
import { ComboBoxState } from '@sas-dvr/internal-va-react-core/store/birdElement/comboBoxState';
import { PromptElementImplInterface } from './PromptElementImplInterface';
import { getFormattedData, getRequired } from './utils';
import { AnyAction } from 'redux';

function rowIndiciesFromIndex(index: number | null | undefined) {
  return index === null || index === undefined ? [] : [index];
}

export class ComboBoxImpl implements PromptElementImplInterface {
  private _controller: PromptElementController;
  private _id: string;
  private _ignoreValueChange: boolean = false;

  constructor(controller: PromptElementController, id: string) {
    this._controller = controller;
    this._id = id;
  }

  createElement() {
    const indices = this._controller.getCurrentValuesInTermsOfOrderedUniqueRowIndices();

    const data = getFormattedData(this._controller);
    const required = getRequired(this._controller);

    const index = indices ? indices[0] : null;

    return ComboBoxState.actions.add(this._id, {
      required,
      label: data.label,
      name: data.name,
      valueColumnLabel: data.valueColumnLabel,
      measureColumnLabel: data.measureColumnLabel,
      values: data.values,
      measureValues: data.measureValues,
      selectedIndex: index,
    });
  }

  deleteElement() {
    return ComboBoxState.actions.delete(this._id);
  }

  handleDataChange(dataChange$: Observable<DataStateChangeEvent>) {
    return dataChange$.pipe(
      map(() => {
        const data = getFormattedData(this._controller);
        const indices = this._controller.getCurrentValuesInTermsOfOrderedUniqueRowIndices();

        // TODO: SelectItem (or some other action) should allow for index: null | undefined
        const index = indices ? indices[0] : ((null as unknown) as number);

        return ComboBoxState.actions.update(this._id, {
          valueColumnLabel: data.valueColumnLabel,
          measureColumnLabel: data.measureColumnLabel,
          values: data.values,
          measureValues: data.measureValues,
          selectedIndex: index,
        });
      })
    );
  }

  handleValueChanged(valueChange$: Observable<PortableEvent>) {
    return valueChange$.pipe(
      mergeMap(() => {
        if (this._ignoreValueChange) {
          return EMPTY;
        }
        const indices = this._controller.getCurrentValuesInTermsOfOrderedUniqueRowIndices();

        // TODO: SelectItem (or some other action) should allow for index: null | undefined
        const index = indices ? indices[0] : ((null as unknown) as number);

        return of(ComboBoxState.actions.update(this._id, { selectedIndex: index }));
      })
    );
  }

  handleActions(_myActions$: Observable<AnyAction>, state$: StateObservable<ReportState>) {
    return state$.pipe(
      map((aState) => aState.birdElements.ComboBox[this._id]),
      filter((myState): myState is Exclude<typeof myState, undefined> => myState !== undefined),
      map((myState) => myState.selectedIndex),
      distinctUntilChanged(),
      // Skip the initial actions with null values. There's no need to update the controller for these,
      // and they prevent us from getting the first real valueChanged event.
      skipWhile((val) => val === null || val === undefined),
      tap((selectedIndex) => {
        this._ignoreValueChange = true;
        // FIXME: We probably shouldn't set the index on the controller if the action that changed
        // the index came from portable.
        this._controller.setCurrentValuesInTermsOfOrderedUniqueRowIndices(
          rowIndiciesFromIndex(selectedIndex)
        );
        this._ignoreValueChange = false;
      }),
      ignoreElements()
    );
  }
}
